import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { auth, requestFCMToken, onForegroundMessage } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
// Direct imports — no lazy loading, instant page transitions
import Landing from './pages/Landing';
import CustomerHome from './pages/CustomerHome';
import AppLogin from './pages/AppLogin';
import AppHome from './pages/AppHome';
import AppSalonDetail from './pages/AppSalonDetail';
import AppBooking from './pages/AppBooking';
import AppBookingDone from './pages/AppBookingDone';
import AppProfile from './pages/AppProfile';
import AppPartnerDashboard from './pages/AppPartnerDashboard';
import AppListSalon from './pages/AppListSalon';
import AppSearch from './pages/AppSearch';
import AppBookings from './pages/AppBookings';
import AppEditProfile from './pages/AppEditProfile';
import AppNotifications from './pages/AppNotifications';
import AppSaved from './pages/AppSaved';
import AppReviews from './pages/AppReviews';
import AppAddresses from './pages/AppAddresses';
import AppOffers from './pages/AppOffers';
import StyleFinder from './pages/StyleFinder';
import CustomerProfile from './pages/CustomerProfile';
import SalonDashboard from './pages/SalonDashboard';
import SalonRegistration from './pages/SalonRegistration';
import SalonPartnerSetup from './pages/SalonPartnerSetup';
import SalonPartnerLogin from './pages/SalonPartnerLogin';
import SalonPendingApproval from './pages/SalonPendingApproval';
import AdminLogin from './pages/AdminLogin';
import EnhancedAdminDashboard from './pages/EnhancedAdminDashboard';
import AboutUs from './pages/AboutUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import HelpCenter from './pages/HelpCenter';
import RotatingText from './components/RotatingText';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Admin phone number for route protection - verified in AdminLogin.js
// const ADMIN_PHONE = '+916205777957';

// Premium Splash / Loading Screen
const LoadingScreen = ({ message = 'Loading...' }) => (
  <div
    style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #fff5f5 0%, #fff 45%, #fdf4ff 100%)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 0,
    }}
  >
    {/* Decorative blobs */}
    <div style={{
      position: 'absolute', top: '-80px', right: '-80px',
      width: '320px', height: '320px', borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(220,38,38,0.08) 0%, transparent 70%)',
      pointerEvents: 'none',
    }} />
    <div style={{
      position: 'absolute', bottom: '-60px', left: '-60px',
      width: '260px', height: '260px', borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(147,51,234,0.06) 0%, transparent 70%)',
      pointerEvents: 'none',
    }} />

    {/* Logo ring */}
    <div style={{ position: 'relative', marginBottom: '28px' }}>
      {/* Spinning ring */}
      <div style={{
        position: 'absolute', inset: '-12px',
        borderRadius: '50%',
        border: '2.5px solid transparent',
        borderTopColor: '#dc2626',
        borderRightColor: '#e11d48',
        animation: 'spin 1.1s linear infinite',
      }} />
      {/* Outer glow ring */}
      <div style={{
        position: 'absolute', inset: '-20px',
        borderRadius: '50%',
        border: '1.5px solid rgba(220,38,38,0.15)',
        animation: 'spin 2.2s linear infinite reverse',
      }} />
      {/* Logo icon */}
      <div style={{
        width: '72px', height: '72px', borderRadius: '50%',
        background: 'linear-gradient(135deg, #dc2626, #e11d48)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 8px 32px rgba(220,38,38,0.35)',
        animation: 'pulse 2s ease-in-out infinite',
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/>
          <line x1="20" y1="4" x2="8.12" y2="15.88"/>
          <line x1="14.47" y1="14.48" x2="20" y2="20"/>
          <line x1="8.12" y1="8.12" x2="12" y2="12"/>
        </svg>
      </div>
      {/* Yellow dot */}
      <div style={{
        position: 'absolute', bottom: '2px', right: '2px',
        width: '14px', height: '14px', borderRadius: '50%',
        background: '#facc15',
        border: '2px solid white',
        boxShadow: '0 2px 6px rgba(250,204,21,0.5)',
      }} />
    </div>

    {/* Brand name */}
    <div style={{ textAlign: 'center', animation: 'fadeUp 0.6s 0.1s both' }}>
      <h1 style={{
        fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.03em',
        fontFamily: "'Syne', sans-serif",
        background: 'linear-gradient(135deg, #dc2626, #e11d48, #9333ea)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        backgroundClip: 'text', marginBottom: '2px',
      }}>
        BookYourSalons
      </h1>
      <p style={{
        fontSize: '0.78rem', color: '#9ca3af', fontWeight: 500,
        letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '6px',
      }}>
        Your Style Awaits ✦
      </p>
    </div>

    {/* Loading dots */}
    <div style={{ marginTop: '32px', display: 'flex', gap: '6px', animation: 'fadeIn 0.5s 0.3s both' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: '7px', height: '7px', borderRadius: '50%',
          background: '#dc2626', opacity: 0.7,
          animation: `bounce-dot 1.4s infinite ease-in-out`,
          animationDelay: `${[-0.32, -0.16, 0][i]}s`,
        }} />
      ))}
    </div>
  </div>
);

// Auto-redirect wrapper for Landing page
function LandingWithAutoRedirect() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    const firebaseUid = localStorage.getItem('firebaseUid');
    const partnerStatus = localStorage.getItem('partnerStatus');

    // Wait for Firebase to confirm the real auth state before redirecting.
    // Redirecting purely on stale localStorage caused a /customer <-> / loop
    // whenever the saved login token had expired (securetoken 400).
    const minSplash = new Promise(resolve => setTimeout(resolve, 1500));
    const authCheck = new Promise(resolve => {
      const unsub = onAuthStateChanged(auth, (firebaseUser) => {
        unsub();
        if (firebaseUser && firebaseUid && userRole) {
          // Real, valid login — safe to send them to their area
          if (userRole === 'customer') {
            navigate('/customer', { replace: true });
          } else if (userRole === 'salon') {
            if (partnerStatus === 'active') {
              navigate('/salon/dashboard', { replace: true });
            } else if (partnerStatus === 'pending') {
              navigate('/salon/pending', { replace: true });
            }
          }
        } else if (!firebaseUser && (firebaseUid || userRole)) {
          // Saved session but Firebase says logged out → token expired.
          // Clear the stale flags so no route bounces us in a loop.
          localStorage.removeItem('firebaseUid');
          localStorage.removeItem('userRole');
          localStorage.removeItem('partnerStatus');
        }
        resolve();
      });
    });

    Promise.all([minSplash, authCheck]).then(() => setChecking(false));
  }, [navigate]);

  if (checking) {
    return <LoadingScreen />;
  }

  return <Landing />;
}

const readSession = () => ({
  isAdmin: localStorage.getItem('isAdmin'),
  adminAuthTime: localStorage.getItem('adminAuthTime'),
  partnerStatus: localStorage.getItem('partnerStatus'),
  userRole: localStorage.getItem('userRole'),
  firebaseUid: localStorage.getItem('firebaseUid'),
});

function App() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  // Session flags mirrored into state so route guards re-evaluate
  // immediately after a login writes to localStorage.
  const [session, setSession] = useState(readSession);

  // Keep session flags in sync with localStorage.
  // - 'storage' event: changes from other tabs
  // - 'session-change' custom event: same-tab logins (dispatched by login pages)
  // - focus + short poll: safety net so a guard can never stay stale
  useEffect(() => {
    // Only update state when a value ACTUALLY changed. Without this guard
    // readSession() returns a fresh object every tick, React sees a new
    // reference, and the entire app re-renders once per second.
    const sync = () => {
      setSession((prev) => {
        const next = readSession();
        const same =
          prev.isAdmin === next.isAdmin &&
          prev.adminAuthTime === next.adminAuthTime &&
          prev.partnerStatus === next.partnerStatus &&
          prev.userRole === next.userRole &&
          prev.firebaseUid === next.firebaseUid;
        return same ? prev : next; // same reference → no re-render
      });
    };

    window.addEventListener('storage', sync);
    window.addEventListener('session-change', sync);
    window.addEventListener('focus', sync);
    const poll = setInterval(sync, 2000);

    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('session-change', sync);
      window.removeEventListener('focus', sync);
      clearInterval(poll);
    };
  }, []);

  useEffect(() => {
    // Minimum 5 second splash screen
    const splashTimer = setTimeout(() => {}, 3000);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const role = localStorage.getItem('userRole');
        setUserRole(role);
        setSession(readSession());
        
        // Request FCM token for push notifications (salon partners)
        if (role === 'salon') {
          try {
            const fcmToken = await requestFCMToken();
            if (fcmToken) {
              console.log('FCM Token obtained');
              localStorage.setItem('fcmToken', fcmToken);
            }
          } catch (e) {
            console.log('FCM not available');
          }
        }
      } else {
        setUser(null);
        setUserRole(null);
      }
      // Wait minimum 5 seconds before hiding splash
      setTimeout(() => setLoading(false), 5000);
    });

    // Listen for foreground messages
    onForegroundMessage((payload) => {
      if (payload.notification) {
        const { title, body } = payload.notification;
        import('sonner').then(({ toast }) => {
          toast(title, { description: body });
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  // ── Session flags ──────────────────────────────────────────
  // These are read from localStorage but stored in state (see the
  // authTick effect above) so that a login writing to localStorage
  // triggers a re-render. Previously these were plain consts read
  // during render, which meant navigate() after login rendered with
  // STALE values and the route guard bounced the user straight back out.
  const adminAuthTime = session.adminAuthTime;
  const isAdminSessionValid = adminAuthTime &&
    (Date.now() - parseInt(adminAuthTime)) < 24 * 60 * 60 * 1000;
  const isAdmin = session.isAdmin === 'true' && isAdminSessionValid;
  const partnerStatus = session.partnerStatus;
  const hasSalonAccess = (user && userRole === 'salon') || partnerStatus === 'active';

  return (
    <ErrorBoundary>
      <HelmetProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<LandingWithAutoRedirect />} />
          <Route path="/customer" element={user && userRole === 'customer' ? <CustomerHome /> : <Navigate to="/" />} />
          <Route path="/style-finder" element={<StyleFinder />} />
          <Route path="/app/login" element={<AppLogin />} />
          <Route path="/app/home" element={<AppHome />} />
          <Route path="/app/salon/:id" element={<AppSalonDetail />} />
          <Route path="/app/book" element={<AppBooking />} />
          <Route path="/app/done" element={<AppBookingDone />} />
          <Route path="/app/profile" element={<AppProfile />} />
          <Route path="/app/partner" element={<AppPartnerDashboard />} />
          <Route path="/app/list-salon" element={<AppListSalon />} />
          <Route path="/app/search" element={<AppSearch />} />
          <Route path="/app/bookings" element={<AppBookings />} />
          <Route path="/app/edit-profile" element={<AppEditProfile />} />
          <Route path="/app/notifications" element={<AppNotifications />} />
          <Route path="/app/saved" element={<AppSaved />} />
          <Route path="/app/reviews" element={<AppReviews />} />
          <Route path="/app/addresses" element={<AppAddresses />} />
          <Route path="/app/offers" element={<AppOffers />} />
          <Route path="/customer/profile" element={user && userRole === 'customer' ? <CustomerProfile /> : <Navigate to="/" />} />
          <Route path="/salon/partner-setup" element={<SalonPartnerSetup />} />
          <Route path="/salon/login" element={<SalonPartnerLogin />} />
          <Route path="/salon/register" element={<SalonRegistration />} />
          <Route path="/salon/pending" element={<SalonPendingApproval />} />
          <Route path="/salon/dashboard" element={hasSalonAccess ? <SalonDashboard /> : <Navigate to="/salon/pending" />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Navigate to="/admin/login" />} />
          <Route path="/admin/dashboard" element={isAdmin ? <EnhancedAdminDashboard /> : <Navigate to="/admin/login" />} />
          {/* Catch-all route for 404 */}
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;